/**
 * HeroBackground — simplified ambient neural-network mesh
 * for subpage hero sections. Lighter than the full HeroDiagram.
 */
const NODES = [
  { x: 60, y: 35 }, { x: 180, y: 55 }, { x: 310, y: 30 },
  { x: 440, y: 50 }, { x: 570, y: 25 }, { x: 700, y: 45 },
  { x: 120, y: 110 }, { x: 250, y: 95 }, { x: 380, y: 115 },
  { x: 510, y: 90 }, { x: 640, y: 105 },
  { x: 80, y: 175 }, { x: 200, y: 160 }, { x: 340, y: 180 },
  { x: 470, y: 155 }, { x: 600, y: 170 }, { x: 730, y: 150 },
  { x: 150, y: 230 }, { x: 290, y: 240 }, { x: 420, y: 220 },
  { x: 560, y: 235 }, { x: 680, y: 215 },
];

const EDGES: [number, number, number][] = [
  [0, 1, 0.12], [1, 2, 0.10], [2, 3, 0.14], [3, 4, 0.10], [4, 5, 0.12],
  [6, 7, 0.14], [7, 8, 0.12], [8, 9, 0.10], [9, 10, 0.14],
  [11, 12, 0.10], [12, 13, 0.12], [13, 14, 0.14], [14, 15, 0.10], [15, 16, 0.12],
  [17, 18, 0.12], [18, 19, 0.10], [19, 20, 0.14], [20, 21, 0.10],
  [0, 6, 0.08], [1, 7, 0.06], [2, 8, 0.08], [3, 9, 0.06], [4, 10, 0.08],
  [6, 12, 0.06], [7, 13, 0.08], [8, 14, 0.06], [9, 15, 0.08], [10, 16, 0.06],
  [11, 17, 0.08], [12, 18, 0.06], [13, 19, 0.08], [14, 20, 0.06], [15, 21, 0.08],
  [1, 8, 0.04], [3, 10, 0.04], [7, 14, 0.04], [9, 16, 0.04],
];

const PARTICLE_ROUTES = [
  [0, 1, 7, 13, 19],
  [5, 4, 9, 14, 18],
  [11, 12, 8, 3, 4],
];

const HeroBackground = ({ className = "" }: { className?: string }) => (
  <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
    <div
      className="absolute inset-0"
      style={{
        background: `
          radial-gradient(ellipse at 60% 40%, hsl(var(--primary) / 0.07) 0%, transparent 55%),
          radial-gradient(ellipse at 30% 70%, hsl(var(--primary) / 0.04) 0%, transparent 45%)
        `,
      }}
    />
    <svg
      viewBox="0 0 780 260"
      className="absolute inset-0 w-full h-full"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
    >
      {/* Edges */}
      {EDGES.map(([a, b, op], i) => {
        const from = NODES[a];
        const to = NODES[b];
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        return (
          <path
            key={`e-${i}`}
            d={`M${from.x},${from.y} C${from.x + dx * 0.4},${from.y + dy * 0.1} ${from.x + dx * 0.6},${to.y - dy * 0.1} ${to.x},${to.y}`}
            stroke="hsl(var(--primary))"
            strokeWidth="0.5"
            opacity={op}
          />
        );
      })}

      {/* Particles */}
      {PARTICLE_ROUTES.map((route, pi) => {
        const pts = route.map((i) => NODES[i]);
        const d = pts
          .map((n, i) => {
            if (i === 0) return `M${n.x},${n.y}`;
            const prev = pts[i - 1];
            const dx = n.x - prev.x;
            const dy = n.y - prev.y;
            return `C${prev.x + dx * 0.4},${prev.y + dy * 0.1} ${prev.x + dx * 0.6},${n.y - dy * 0.1} ${n.x},${n.y}`;
          })
          .join(" ");
        const id = `hb-p-${pi}`;
        const dur = 5 + pi * 0.8;
        return (
          <g key={id}>
            <path id={id} d={d} fill="none" stroke="none" />
            <circle r="1.5" fill="hsl(var(--primary))">
              <animateMotion dur={`${dur}s`} repeatCount="indefinite" begin={`${pi * 1.5}s`}>
                <mpath href={`#${id}`} />
              </animateMotion>
              <animate attributeName="opacity" values="0;0.6;0.6;0" dur={`${dur}s`} repeatCount="indefinite" begin={`${pi * 1.5}s`} />
            </circle>
          </g>
        );
      })}

      {/* Nodes */}
      {NODES.map((n, i) => (
        <g key={`n-${i}`}>
          <circle cx={n.x} cy={n.y} r="8" stroke="hsl(var(--primary))" strokeWidth="0.3" fill="none" opacity="0.06">
            <animate attributeName="opacity" values="0.04;0.12;0.04" dur="5s" repeatCount="indefinite" begin={`${i * 0.2}s`} />
          </circle>
          <circle cx={n.x} cy={n.y} r="1.5" fill="hsl(var(--primary))" opacity="0.2">
            <animate attributeName="opacity" values="0.15;0.35;0.15" dur="4s" repeatCount="indefinite" begin={`${i * 0.15}s`} />
          </circle>
        </g>
      ))}

      {/* Hex grid dots */}
      {Array.from({ length: 4 }).map((_, row) =>
        Array.from({ length: 14 }).map((_, col) => {
          const x = col * 58 + (row % 2 ? 29 : 0);
          const y = row * 68 + 15;
          return (
            <circle key={`h-${row}-${col}`} cx={x} cy={y} r="0.5" fill="hsl(var(--primary))" opacity="0.03" />
          );
        })
      )}
    </svg>
  </div>
);

export default HeroBackground;
